// 📁 modules/editor.js

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast, Flip } from 'react-toastify';
import api from '@utils/axiosInstance';

// --- Action Types ---
const PURGE = "editor/PURGE";
const SET_EDIT_PROJECT_IDX = "editor/SET_EDIT_PROJECT_IDX";
const SET_EDIT_PROJECT_LIST = "editor/SET_EDIT_PROJECT_LIST";
const SET_PROJECT_DATA = "editor/SET_PROJECT_DATA";
const SET_PROJECT_INFO = "editor/SET_PROJECT_INFO";
const SET_EDIT_TOOL_IDX = "editor/SET_EDIT_TOOL_IDX";
const SET_START_END = "editor/SET_START_END";
const SET_CHANGED_TIMELINE_CUT_LIST = "editor/SET_CHANGED_TIMELINE_CUT_LIST";
const SET_CHANGED_ROW_CUT_LIST = "editor/SET_CHANGED_ROW_CUT_LIST";
const SET_EDIT_HISTORY = 'editor/SET_EDIT_HISTORY';
const SET_EDIT_HISTORY_FLAG = 'editor/SET_EDIT_HISTORY_FLAG';
const CLEAR_EDIT_HISTORY = 'editor/CLEAR_EDIT_HISTORY';
const SET_CUTLIST = 'cutlist/SET_CUTLIST';
const ADD_CUT = 'cutlist/ADD_CUT';
const UPDATE_CUT = 'cutlist/UPDATE_CUT';
const DELETE_CUT = 'cutlist/DELETE_CUT';
const SET_WATERMARK_LIST = 'editor/SET_WATERMARK_LIST';
const ADD_WATERMARK = 'editor/ADD_WATERMARK';
const UPDATE_WATERMARK = 'editor/UPDATE_WATERMARK';
const DELETE_WATERMARK = 'editor/DELETE_WATERMARK';
const SET_CROP_LIST = 'editor/SET_CROP_LIST';
const ADD_CROP = 'editor/ADD_CROP';
const UPDATE_CROP = 'editor/UPDATE_CROP';
const DELETE_CROP = 'editor/DELETE_CROP';
const SET_CONCAT_LIST = 'editor/SET_CONCAT_LIST';
const ADD_CONCAT = 'editor/ADD_CONCAT';
const UPDATE_CONCAT = 'editor/UPDATE_CONCAT';
const DELETE_CONCAT = 'editor/DELETE_CONCAT';
const SET_EDITING_STATE = 'editor/SET_EDITING_STATE';
const RESET_EDITING_STATE = 'editor/RESET_EDITING_STATE';
const SET_LAST_SAVED_DATA = 'editor/SET_LAST_SAVED_DATA';
const SET_CANVAS_SIZE = 'editor/SET_CANVAS_SIZE';
const UPDATE_PROJECT_NAME = 'editor/UPDATE_PROJECT_NAME';

// --- Action Creators ---
export const purgeEditor = () => ({ type: PURGE });
export const setEditProjIdx = (editProjIdx: any) => ({ type: SET_EDIT_PROJECT_IDX, editProjIdx });
export const setEditProjList = (editProjList: any) => ({ type: SET_EDIT_PROJECT_LIST, editProjList });
export const setProjectData = (projectData: any) => ({ type: SET_PROJECT_DATA, projectData });
export const setProjectInfo = (projectInfo: any) => ({ type: SET_PROJECT_INFO, projectInfo });
export const setEditToolIdx = (editToolIdx: any) => ({ type: SET_EDIT_TOOL_IDX, editToolIdx });
export const setStartEnd = (startEnd: any) => ({ type: SET_START_END, startEnd });
export const setChangedTimelineCutList = (changedTimelineCutList: any) => ({ type: SET_CHANGED_TIMELINE_CUT_LIST, changedTimelineCutList });
export const setChangedRowCutList = (changedRowCutList: any) => ({ type: SET_CHANGED_ROW_CUT_LIST, changedRowCutList });
export const setEditHistory = (editHistory: any) => ({ type: SET_EDIT_HISTORY, editHistory });
export const setEditHistoryFlag = (editHistoryFlag: any) => ({ type: SET_EDIT_HISTORY_FLAG, editHistoryFlag });
export const clearEditHistory = () => ({ type: CLEAR_EDIT_HISTORY });
export const setCutlist = (cut: any) => ({ type: SET_CUTLIST, cut });
export const addCut = (cut: any) => ({ type: ADD_CUT, cut });
export const updateCut = (cut: any) => ({ type: UPDATE_CUT, cut });
export const deleteCut = (cutIdx: any) => ({ type: DELETE_CUT, cutIdx });

export const setWatermarkList = (watermarkList: any) => ({ type: SET_WATERMARK_LIST, watermarkList });
export const addWatermark = (watermark: any) => ({ type: ADD_WATERMARK, watermark });
export const updateWatermark = (watermark: any) => ({ type: UPDATE_WATERMARK, watermark });
export const deleteWatermark = (watermarkDataIdx: any) => ({ type: DELETE_WATERMARK, watermarkDataIdx });

export const setCropList = (cropList: any) => ({ type: SET_CROP_LIST, cropList });
export const addCrop = (crop: any) => ({ type: ADD_CROP, crop });
export const updateCrop = (crop: any) => ({ type: UPDATE_CROP, crop });
export const deleteCrop = () => ({ type: DELETE_CROP });

export const setConcatList = (concatList: any) => ({ type: SET_CONCAT_LIST, concatList });
export const addConcat = (concat: any) => ({ type: ADD_CONCAT, concat });
export const updateConcat = (concat: any) => ({ type: UPDATE_CONCAT, concat });
export const deleteConcat = (concatDataIdx: any) => ({ type: DELETE_CONCAT, concatDataIdx });

export const setEditingState = (component: string, isEditing: boolean) => ({
  type: SET_EDITING_STATE,
  component,
  isEditing
});

export const resetEditingState = () => ({ type: RESET_EDITING_STATE });

export const setLastSavedData = (lastSavedData: any) => ({
  type: SET_LAST_SAVED_DATA,
  lastSavedData
});

export const setCanvasSize = (canvasSize: { canvasWidth: number; canvasHeight: number }) => ({
  type: SET_CANVAS_SIZE,
  canvasSize
});

export const updateProjectName = (newName: string) => ({
  type: UPDATE_PROJECT_NAME,
  newName
});

// --- Custom Hooks ---

export function useEditorPublish() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isPublishing, setIsPublishing] = useState(false);

  const userToken = useSelector((state: any) => state.organization.userToken);
  const organizationData = useSelector((state: any) => state.organization.organizationData);
  const isUpdatePendingRedux = useSelector((state: any) => state.editor.isUpdatePending);

  const handlePublish = async (uuid: string, editProjectId: string) => {
    if (isUpdatePendingRedux) {
      window.alert('업데이트가 아직 완료되지 않았습니다. 잠시 후 다시 시도해 주세요.');
      return;
    }
    if (isPublishing) return;

    setIsPublishing(true);
    try {
      await api({
        method: 'post',
        url: '/editor/publish',
        data: {
          tk: userToken,
          uuid: uuid,
          ogc: organizationData.organization_code,
          project_id: editProjectId
        }
      });
      toast.success('발행이 완료되었습니다.', {
        position: 'top-center',
        autoClose: 3000,
        transition: Flip
      });
    } catch (error) {
      console.log(error);
      toast.error('발행 중 오류가 발생했습니다.', {
        position: 'top-center',
        autoClose: 3000,
        transition: Flip
      });
    } finally {
      setIsPublishing(false);
      navigate(`/vod/${uuid}/editor`);
    }
  };

  return {
    isPublishing,
    isUpdatePendingRedux,
    handlePublish
  };
}

export function useEditorHistory() {
  const dispatch = useDispatch();
  const editHistory = useSelector((state: any) => state.editor.editHistory);
  const editHistoryFlag = useSelector((state: any) => state.editor.editHistoryFlag);

  const undo = () => {
    if (editHistoryFlag > 0) {
      dispatch(setEditHistoryFlag(-1));
      dispatch(setCutlist(editHistory[editHistoryFlag - 1]));
    }
  };

  const redo = () => {
    if (editHistoryFlag < editHistory.length - 1) {
      dispatch(setEditHistoryFlag(1));
      dispatch(setCutlist(editHistory[editHistoryFlag + 1]));
    }
  };

  return {
    editHistory,
    editHistoryFlag,
    undo,
    redo
  };
}

// --- Initial State ---
const initialState = {
  editProjIdx: -1,
  editProjList: [],
  projectData: [],
  projectInfo: [],
  editToolIdx: -1,
  watermarkList: [],
  concatList: {},
  isCrop: false,
  cropList: {
    prev: { start: 0, end: 0 },
    current: { start: 0, end: 0 }
  },
  startEnd: { start: 0, end: 0 },
  cutList: [],
  changedTimelineCutList: [],
  changedRowCutList: {},
  editHistory: [],
  editHistoryFlag: -1,
  initialCutlistState: [],
  // 변경 추적을 위한 상태
  editingState: {
    cut: false,
    concat: false,
    watermark: false,
    crop: false
  },
  // 마지막 저장 시점의 데이터
  lastSavedData: {
    cut: [],
    concat: {},
    watermark: [],
    crop: []
  },
  // 캔버스 크기 정보
  canvasSize: {
    canvasWidth: 0,
    canvasHeight: 0
  }
};

// --- Reducer ---
export default function editor(state = initialState, action) {
  switch (action.type) {
    case PURGE:
      return { ...initialState };
    case SET_EDIT_PROJECT_IDX:
      return { ...state, editProjIdx: action.editProjIdx };
    case SET_EDIT_PROJECT_LIST:
      return { ...state, editProjList: action.editProjList };
    case SET_PROJECT_DATA:
      return { ...state, projectData: action.projectData };
    case SET_PROJECT_INFO:
      return { ...state, projectInfo: action.projectInfo };
    case SET_EDIT_TOOL_IDX:
      return { ...state, editToolIdx: action.editToolIdx };
    case SET_START_END:
      return { ...state, startEnd: action.startEnd };
    case SET_CHANGED_TIMELINE_CUT_LIST:
      return { ...state, changedTimelineCutList: action.changedTimelineCutList };
    case SET_CHANGED_ROW_CUT_LIST:
      return { ...state, changedRowCutList: action.changedRowCutList };
    case SET_EDIT_HISTORY:
      console.log('action.editHistory', state.editHistory, action.editHistory, [...state.editHistory, action.editHistory]);
      return { ...state, editHistory: [...state.editHistory, action.editHistory] };
    case SET_EDIT_HISTORY_FLAG:
      console.log('action.flag', state.editHistoryFlag, action.editHistoryFlag);
      return { ...state, editHistoryFlag: state.editHistoryFlag + action.editHistoryFlag };
    case CLEAR_EDIT_HISTORY:
      return { ...state, editHistory: [], editHistoryFlag: 0 };
    case SET_CUTLIST:
      return { ...state, cutList: action.cut };
    case ADD_CUT:
      return {
        ...state,
        cutList: [...state.cutList, action.cut],
        editingState: { ...state.editingState, cut: true }
      };
    case UPDATE_CUT:
      return {
        ...state,
        cutList: state.cutList.map((cut, index) =>
          index === action.cut.regionIndex ? action.cut : cut
        ),
        editingState: { ...state.editingState, cut: true }
      };
    case DELETE_CUT:
      return {
        ...state,
        cutList: state.cutList.filter((_, index) => index !== action.cutIdx),
        editingState: { ...state.editingState, cut: true }
      };
    case SET_WATERMARK_LIST:
      return { ...state, watermarkList: action.watermarkList };
    case ADD_WATERMARK:
      return {
        ...state,
        watermarkList: [...state.watermarkList, action.watermark],
        editingState: { ...state.editingState, watermark: true }
      };
    case UPDATE_WATERMARK:
      return {
        ...state,
        watermarkList: state.watermarkList.map((item, index) =>
          index === action.watermark.index ? action.watermark : item
        ),
        editingState: { ...state.editingState, watermark: true }
      };
    case DELETE_WATERMARK:
      return {
        ...state,
        watermarkList: state.watermarkList.filter((_, index) => index !== action.watermarkDataIdx),
        editingState: { ...state.editingState, watermark: true }
      };
    case SET_CROP_LIST:
      return { ...state, cropList: action.cropList };
    case ADD_CROP:
      return {
        ...state,
        cropList: action.crop,
        editingState: { ...state.editingState, crop: true }
      };
    case UPDATE_CROP:
      return {
        ...state,
        cropList: { ...state.cropList, ...action.crop },
        editingState: { ...state.editingState, crop: true }
      };
    case DELETE_CROP:
      return {
        ...state,
        cropList: {},
        editingState: { ...state.editingState, crop: true }
      };
    case SET_CONCAT_LIST:
      return { ...state, concatList: action.concatList };
    case ADD_CONCAT:
      return {
        ...state,
        concatList: [...state.concatList, action.concat],
        editingState: { ...state.editingState, concat: true }
      };
    case UPDATE_CONCAT:
      return {
        ...state,
        concatList: state.concatList.map((item, index) =>
          index === action.concat.index ? action.concat : item
        ),
        editingState: { ...state.editingState, concat: true }
      };
    case DELETE_CONCAT:
      return {
        ...state,
        concatList: state.concatList.filter((_, index) => index !== action.concatDataIdx),
        editingState: { ...state.editingState, concat: true }
      };
    case SET_EDITING_STATE:
      return {
        ...state,
        editingState: {
          ...state.editingState,
          [action.component]: action.isEditing
        }
      };
    case RESET_EDITING_STATE:
      return {
        ...state,
        editingState: {
          cut: false,
          concat: false,
          watermark: false,
          crop: false
        }
      };
    case SET_LAST_SAVED_DATA:
      return { ...state, lastSavedData: action.lastSavedData };
    case SET_CANVAS_SIZE:
      return { ...state, canvasSize: action.canvasSize };
    case UPDATE_PROJECT_NAME:
      return {
        ...state,
        projectInfo: state.projectInfo.map((project: any) => ({
          ...project,
          label: action.newName
        }))
      };
    default:
      return state;
  }
}
